import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Save component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconSave extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.00745 1.32416C6.1481 1.18351 6.33886 1.10449 6.53778 1.10449H8.09863H17.4632H20.5847C21.1976 1.10449 21.7853 1.34795 22.2187 1.7813C22.652 2.21465 22.8955 2.80241 22.8955 3.41526V20.5837C22.8955 21.1966 22.652 21.7843 22.2187 22.2177C21.7853 22.651 21.1976 22.8945 20.5847 22.8945H17.4794L17.4632 22.8947H6.53784L6.52163 22.8945H3.41624C2.80338 22.8945 2.21563 22.651 1.78228 22.2177C1.34892 21.7843 1.10547 21.1966 1.10547 20.5837V6.5368C1.10547 6.33789 1.18449 6.14712 1.32514 6.00647L6.00745 1.32416ZM16.7132 2.60449H8.84863V7.34757H16.7132V2.60449ZM7.34863 2.60449V8.09757C7.34863 8.51178 7.68442 8.84757 8.09863 8.84757H17.4632C17.8775 8.84757 18.2132 8.51178 18.2132 8.09757V2.60449H20.5847C20.7997 2.60449 21.006 2.68991 21.158 2.84196C21.31 2.99401 21.3955 3.20023 21.3955 3.41526V20.5837C21.3955 20.7988 21.31 21.005 21.158 21.157C21.006 21.3091 20.7997 21.3945 20.5847 21.3945H18.2132V14.3408C18.2132 13.9266 17.8774 13.5908 17.4632 13.5908H6.53784C6.12363 13.5908 5.78784 13.9266 5.78784 14.3408V21.3945H3.41624C3.20121 21.3945 2.99499 21.3091 2.84294 21.157C2.69089 21.005 2.60547 20.7988 2.60547 20.5837V6.84746L6.84844 2.60449H7.34863ZM7.28784 21.3945H16.7132V15.0908H7.28784V21.3945Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-save') === undefined) {
  customElements.define('cat-icon-save', BlocksIconSave);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-save': BlocksIconSave;
  }
}
